import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import type { CoachingPlan, CommitmentOption } from "../types/index";
import { COACHING_PLANS, COMMITMENT_OPTIONS } from "../data/coachingData";
import { type MarketCode } from "./countryDetectionService";

// Staging & Production Firebase project configurations
const firebaseConfigs = {
  staging: {
    projectId: "everforge-fitness-staging",
    appId: "1:48697165878:web:ef990a1b5fea9945bcb097",
    storageBucket: "everforge-fitness-staging.firebasestorage.app",
    apiKey: "AIzaSyB2NeLA_O8tQM2n9CLCCR5So4NLTcd7pTU",
    authDomain: "everforge-fitness-staging.firebaseapp.com",
    messagingSenderId: "48697165878",
  },
  production: {
    projectId: "everforge-fitness-tracker",
    appId: "1:1068713356422:web:7002e5f7b511578de5f507",
    storageBucket: "everforge-fitness-tracker.firebasestorage.app",
    apiKey: "AIzaSyCNpHhxcmwDJD-IpVHr6BoOkJDm1A0nWO4",
    authDomain: "everforge-fitness-tracker.firebaseapp.com",
    messagingSenderId: "1068713356422",
  },
};

function resolveConfig() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // Explicit staging or local hostnames must strictly use staging Firestore
    if (
      host.includes("staging") ||
      host.includes("localhost") ||
      host === "127.0.0.1"
    ) {
      return firebaseConfigs.staging;
    }
    // Explicit production domains
    if (
      host === "everforgefitness.com" ||
      host === "www.everforgefitness.com" ||
      host === "everforge.web.app" ||
      host === "everforge-fitness-tracker.web.app"
    ) {
      return firebaseConfigs.production;
    }
  }

  // Fallback to explicit environment variable check
  return import.meta.env?.VITE_APP_ENV === "production"
    ? firebaseConfigs.production
    : firebaseConfigs.staging;
}

export interface PublishedRegionalCatalog {
  versionId?: string;
  marketCode: MarketCode;
  currency: string;
  status: 'published' | 'unpublished';
  publishedAt?: string;
  effectiveDate?: string;
  plans: CoachingPlan[];
  commitmentOptions: CommitmentOption[];
}

let firestoreInstance: any = null;

function getDb() {
  if (firestoreInstance) return firestoreInstance;
  try {
    const config = resolveConfig();
    const app = getApps().length > 0 ? getApp() : initializeApp(config);
    firestoreInstance = getFirestore(app);
    return firestoreInstance;
  } catch (err) {
    console.warn("[PricingService] Could not initialize Firebase client:", err);
    return null;
  }
}

// In-memory catalog cache partitioned strictly by marketCode to prevent cross-market leakage
const marketCache = new Map<MarketCode, PublishedRegionalCatalog | null>();

/**
 * Fetches the published pricing catalog for a specific market ('US', 'IN', or 'UNSUPPORTED').
 * Rules:
 * - 'US': Fetches pricing_published/US (or legacy pricing_published/active). Falls back to approved US defaults.
 * - 'IN': Fetches pricing_published/IN. If unpublished or unavailable, returns null (triggers Contact Us).
 *   NEVER falls back from India to US prices.
 * - 'UNSUPPORTED': Always returns null (Contact for regional pricing).
 */
export async function fetchPublishedRegionalCatalog(marketCode: MarketCode): Promise<PublishedRegionalCatalog | null> {
  if (marketCode === 'UNSUPPORTED') {
    return null;
  }

  if (marketCache.has(marketCode)) {
    return marketCache.get(marketCode) || null;
  }

  const db = getDb();
  if (!db) {
    // If db client unavailable:
    // Only US has approved fallback. IN must return null (Contact Us)
    if (marketCode === 'US') {
      const fallbackUs: PublishedRegionalCatalog = {
        marketCode: 'US',
        currency: 'USD',
        status: 'published',
        plans: COACHING_PLANS,
        commitmentOptions: COMMITMENT_OPTIONS,
      };
      marketCache.set('US', fallbackUs);
      return fallbackUs;
    }
    return null;
  }

  try {
    const marketDocRef = doc(db, "pricing_published", marketCode);
    const snap = await getDoc(marketDocRef);

    if (snap.exists()) {
      const data = snap.data();
      // Must be published to be visible
      if (data.status === 'published' && Array.isArray(data.plans) && data.plans.length > 0) {
        const catalog: PublishedRegionalCatalog = {
          versionId: data.versionId,
          marketCode,
          currency: data.currency || (marketCode === 'IN' ? 'INR' : 'USD'),
          status: 'published',
          publishedAt: data.publishedAt,
          effectiveDate: data.effectiveDate,
          plans: data.plans,
          commitmentOptions: Array.isArray(data.commitmentOptions) && data.commitmentOptions.length > 0
            ? data.commitmentOptions
            : COMMITMENT_OPTIONS,
        };
        marketCache.set(marketCode, catalog);
        return catalog;
      }
    }

    // For US only, check legacy pricing_published/active
    if (marketCode === 'US') {
      const legacyDocRef = doc(db, "pricing_published", "active");
      const legacySnap = await getDoc(legacyDocRef);
      if (legacySnap.exists()) {
        const data = legacySnap.data();
        if (data.status !== 'unpublished' && Array.isArray(data.plans) && data.plans.length > 0) {
          const catalog: PublishedRegionalCatalog = {
            versionId: data.versionId,
            marketCode: 'US',
            currency: data.currency || 'USD',
            status: 'published',
            publishedAt: data.publishedAt,
            effectiveDate: data.effectiveDate,
            plans: data.plans,
            commitmentOptions: Array.isArray(data.commitmentOptions) ? data.commitmentOptions : COMMITMENT_OPTIONS,
          };
          marketCache.set('US', catalog);
          return catalog;
        }
      }
    }
  } catch (err) {
    console.warn(`[PricingService] Error fetching ${marketCode} pricing:`, err);
  }

  // Safe Fallback Rule:
  // US defaults to approved USD plans ($65, $129, $229, $299)
  // India and others return null -> Contact Us
  if (marketCode === 'US') {
    const defaultUs: PublishedRegionalCatalog = {
      marketCode: 'US',
      currency: 'USD',
      status: 'published',
      plans: COACHING_PLANS,
      commitmentOptions: COMMITMENT_OPTIONS,
    };
    marketCache.set('US', defaultUs);
    return defaultUs;
  }

  // India unpublished or unsupported market
  marketCache.set(marketCode, null);
  return null;
}

/**
 * Subscribes to real-time updates for a specific market catalog.
 */
export function subscribeToRegionalPricing(
  marketCode: MarketCode,
  callback: (catalog: PublishedRegionalCatalog | null) => void
): () => void {
  if (marketCode === 'UNSUPPORTED') {
    callback(null);
    return () => {};
  }

  const db = getDb();
  if (!db) {
    if (marketCode === 'US') {
      callback({
        marketCode: 'US',
        currency: 'USD',
        status: 'published',
        plans: COACHING_PLANS,
        commitmentOptions: COMMITMENT_OPTIONS,
      });
    } else {
      callback(null);
    }
    return () => {};
  }

  try {
    const marketDocRef = doc(db, "pricing_published", marketCode);
    return onSnapshot(
      marketDocRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.status === 'published' && Array.isArray(data.plans) && data.plans.length > 0) {
            const catalog: PublishedRegionalCatalog = {
              versionId: data.versionId,
              marketCode,
              currency: data.currency || (marketCode === 'IN' ? 'INR' : 'USD'),
              status: 'published',
              publishedAt: data.publishedAt,
              effectiveDate: data.effectiveDate,
              plans: data.plans,
              commitmentOptions: Array.isArray(data.commitmentOptions) ? data.commitmentOptions : COMMITMENT_OPTIONS,
            };
            marketCache.set(marketCode, catalog);
            callback(catalog);
            return;
          }
        }

        // Unpublished or missing
        if (marketCode === 'US') {
          callback({
            marketCode: 'US',
            currency: 'USD',
            status: 'published',
            plans: COACHING_PLANS,
            commitmentOptions: COMMITMENT_OPTIONS,
          });
        } else {
          marketCache.set(marketCode, null);
          callback(null);
        }
      },
      () => {
        if (marketCode === 'US') {
          callback({
            marketCode: 'US',
            currency: 'USD',
            status: 'published',
            plans: COACHING_PLANS,
            commitmentOptions: COMMITMENT_OPTIONS,
          });
        } else {
          callback(null);
        }
      }
    );
  } catch {
    if (marketCode === 'US') {
      callback({
        marketCode: 'US',
        currency: 'USD',
        status: 'published',
        plans: COACHING_PLANS,
        commitmentOptions: COMMITMENT_OPTIONS,
      });
    } else {
      callback(null);
    }
    return () => {};
  }
}

export function _clearMarketCache(): void {
  marketCache.clear();
}
